import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiChevronDown } from "react-icons/fi";
import { fetchCallAllDataByIDAPI } from "../../utils/api";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import withAuth from "../../utils/withAuth";
import ReportNavBar from "../../components/ReportNavBar";
import { numberFormatWithGst } from "../../components/numberFormatWithGst";
import useSkillCenterMap from "../../hooks/useSkillCenterMap";

const SKELETON_COUNT = 10;

const SkeletonTableCard = () => (
    <div className="col-md-4 col-lg-3 col-sm-6 mb-4">
        <div className="card h-100 shadow-sm">
            <div className="card-body p-2">
                <div className="skeleton" style={{ height: 18, width: "70%", marginBottom: 10 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "80%", marginBottom: 6 }} />
            </div>
        </div>
    </div>
);

const perCallCost = (spend, calls) => {
    const c = Number(calls || 0);
    const s = Number(spend || 0);
    if (c <= 0) return 0;
    return s / c;
};

const TodayLiveCall = () => {
    const [process, setProcess] = useState([]);     // API "rows"
    const [summary, setSummary] = useState(null);   // API "summary"
    const [isLoading, setIsLoading] = useState(false);

    // âœ… Date range filters
    const [fromDate, setFromDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [appliedDate, setAppliedDate] = useState({ from: "", to: "" });

    // âœ… Skill search (applied)
    const [skillQuery, setSkillQuery] = useState("");
    const [appliedSkillQuery, setAppliedSkillQuery] = useState("");

    // âœ… Center multi-select (checkbox)
    const [centerOpen, setCenterOpen] = useState(false);
    const [centerSearch, setCenterSearch] = useState("");
    const [selectedCenters, setSelectedCenters] = useState(new Set()); // working
    const [appliedCenters, setAppliedCenters] = useState(new Set());  // applied

    const [showGroups, setShowGroups] = useState(true);

    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");
    const { deriveCenterName } = useSkillCenterMap();
    const isPpc33 = Number(ppcid) === 33;
    const ppc33AllowedSkills = useMemo(
        () => new Set(["Z_HIN_CONSULTATION11_SKS", "X_HIN_AK61_SKS"]),
        []
    );

    const visibleProcessRows = useMemo(() => {
        const baseRows = isPpc33
            ? (process || []).filter((row) => ppc33AllowedSkills.has(String(row?.skillname || "").trim()))
            : (process || []);

        if (!isPpc33) return baseRows;

        const existing = new Set(baseRows.map((row) => String(row?.skillname || "").trim()));
        const missing = Array.from(ppc33AllowedSkills).filter((skill) => !existing.has(skill));
        const seedRows = missing.map((skill) => ({
            skillname: skill,
            process_mobile: "",
            unique_calls: 0,
            order_count: 0,
            spend_today: 0,
        }));

        return [...baseRows, ...seedRows];
    }, [process, isPpc33, ppc33AllowedSkills]);

    useEffect(() => {
        if (!ppcid) return navigate("/");
        navigate("/today-live-call-reports");

        // default load for today
        setAppliedDate({ from: fromDate, to: toDate });
        getLiveCallWithProcessName(ppcid, fromDate, toDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppcid]);

    const getLiveCallWithProcessName = async (id, d1, d2) => {
        setIsLoading(true);
        try {
            const qs = new URLSearchParams();
            if (d1) qs.set("start_date", d1);
            if (d2) qs.set("end_date", d2);

            const res = await fetchCallAllDataByIDAPI(
                `ppc-team-get-live-call-with-proccess-name-by-id/${id}?${qs.toString()}`
            );

            const apiData = res?.data || {};
            const okStatus = Number(apiData?.status) === 1 || String(apiData?.status) === "1";

            if (okStatus) {
                const p = apiData?.process || {};
                const rows = Array.isArray(p?.rows)
                    ? p.rows
                    : Array.isArray(apiData?.rows)
                        ? apiData.rows
                        : Array.isArray(p?.data)
                            ? p.data
                            : [];
                setProcess(rows);
                setSummary(p?.summary || null);

                // âœ… initialize center selections to ALL (only on first load / empty selection)
                // We'll build centers later from grouped data, but we can keep empty now.
            } else {
                toast.error(apiData?.message || "Failed to load process");
                setProcess([]);
                setSummary(null);
            }
        } catch (e) {
            console.error(e);
            toast.error("Network/CORS error while loading process");
            setProcess([]);
            setSummary(null);
        } finally {
            setIsLoading(false);
        }
    };
    const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
    const showProcessMobile = Number(ppcid) === 31;
    const getSkillLabel = (item) => {
        const mobile = String(item?.processMobile || "").trim();
        return showProcessMobile && mobile ? mobile : String(item?.code || "");
    };
    
    // âœ… Group raw data
    const groupedDataRaw = useMemo(() => {
        const groups = {};
        visibleProcessRows.forEach((row) => {
            const code = String(row?.skillname || "");
            const processMobile = String(row?.process_mobile || "").trim();
            const calls = Number(row?.unique_calls || 0);
            const orders = Number(row?.order_count || 0);
            const spend = Number(row?.spend_today || 0);
            const groupName = String(deriveCenterName(code) || "Other").trim() || "Other";

            if (!groups[groupName]) {
                groups[groupName] = { name: groupName, totalCalls: 0, totalOrders: 0, totalSpend: 0, items: [] };
            }

            groups[groupName].totalCalls += calls;
            groups[groupName].totalOrders += orders;
            groups[groupName].totalSpend += spend;

            groups[groupName].items.push({
                code,
                processMobile,
                calls,
                orders,
                spend,
                perCall: perCallCost(spend, calls),
                perf: calls ? (orders / calls) * 100 : 0,
            });
        });

        Object.values(groups).forEach((g) => {
            g.perf = g.totalCalls ? (g.totalOrders / g.totalCalls) * 100 : 0;
            g.perCall = perCallCost(g.totalSpend, g.totalCalls);
            g.items.sort((a, b) => (b.calls !== a.calls ? b.calls - a.calls : a.code.localeCompare(b.code)));
        });

        return Object.values(groups).sort((a, b) =>
            b.totalCalls !== a.totalCalls ? b.totalCalls - a.totalCalls : a.name.localeCompare(b.name)
        );
    }, [visibleProcessRows, deriveCenterName]);

    // âœ… center options (for checkbox list)
    const centerOptions = useMemo(() => {
        return Array.from(new Set(groupedDataRaw.map((g) => g.name).filter(Boolean))).sort((a, b) =>
            a.localeCompare(b)
        );
    }, [groupedDataRaw]);

    const filteredCenterOptions = useMemo(() => {
        const q = String(centerSearch || "").trim().toLowerCase();
        if (!q) return centerOptions;
        return centerOptions.filter((c) => c.toLowerCase().includes(q));
    }, [centerOptions, centerSearch]);

    const toggleCenter = (name) => {
        setSelectedCenters((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    const onApplyCenters = () => {
        setAppliedCenters(new Set(selectedCenters));
        setCenterOpen(false);
    };

    const centerButtonText = useMemo(() => {
        if (!appliedCenters || appliedCenters.size === 0) return "All Centers";
        if (appliedCenters.size === centerOptions.length) return "All Centers";
        if (appliedCenters.size === 1) return Array.from(appliedCenters)[0];
        return `${appliedCenters.size} Centers`;
    }, [appliedCenters, centerOptions]);

    // âœ… apply Center + Skill filter on grouped data
    const groupedData = useMemo(() => {
        const q = String(appliedSkillQuery || "").trim().toLowerCase();
        const centers = appliedCenters && appliedCenters.size
            ? new Set(Array.from(appliedCenters).map((name) => String(name || "").trim().toLowerCase()))
            : null;

        let list = centers
            ? groupedDataRaw.filter((g) => centers.has(String(g?.name || "").trim().toLowerCase()))
            : groupedDataRaw;
        if (!Array.isArray(list)) list = [];

        if (q) {
            list = list
                .map((g) => {
                    const items = (g.items || []).filter((it) =>
                        String(it.code || "").toLowerCase().includes(q) ||
                        String(it.processMobile || "").toLowerCase().includes(q)
                    );
                    if (!items.length) return null;

                    const totalCalls = items.reduce((s, it) => s + (Number(it.calls) || 0), 0);
                    const totalOrders = items.reduce((s, it) => s + (Number(it.orders) || 0), 0);
                    const totalSpend = items.reduce((s, it) => s + (Number(it.spend) || 0), 0);

                    return {
                        ...g,
                        items,
                        totalCalls,
                        totalOrders,
                        totalSpend,
                        perf: totalCalls ? (totalOrders / totalCalls) * 100 : 0,
                        perCall: perCallCost(totalSpend, totalCalls),
                    };
                })
                .filter(Boolean);
        }

        return list;
    }, [groupedDataRaw, appliedCenters, centerOptions, appliedSkillQuery]);

    // Use rows directly so the UI stays in sync with the API payload.
    const liveCallCount = useMemo(
        () => visibleProcessRows.reduce((s, r) => s + (Number(r?.unique_calls) || 0), 0),
        [visibleProcessRows]
    );

    const liveOrderCount = useMemo(
        () => visibleProcessRows.reduce((s, r) => s + (Number(r?.order_count) || 0), 0),
        [visibleProcessRows]
    );

    const liveSpendToday = useMemo(
        () => visibleProcessRows.reduce((s, r) => s + (Number(r?.spend_today) || 0), 0),
        [visibleProcessRows]
    );

    const livePerf = useMemo(
        () => (liveCallCount ? (liveOrderCount / liveCallCount) * 100 : 0),
        [liveCallCount, liveOrderCount]
    );

    const livePerCall = useMemo(() => perCallCost(liveSpendToday, liveCallCount), [liveSpendToday, liveCallCount]);

    // âœ… Apply button for date + filters
    const onApplyFilters = () => {
        const d1 = String(fromDate || "");
        const d2 = String(toDate || "");
        if (!d1 || !d2) return toast.error("Please select From Date and To Date");
        if (d1 > d2) return toast.error("From Date cannot be greater than To Date");

        setAppliedDate({ from: d1, to: d2 });
        setAppliedSkillQuery(skillQuery || "");
        setAppliedCenters(new Set(selectedCenters));
        setCenterOpen(false);

        if (ppcid) getLiveCallWithProcessName(ppcid, d1, d2);
    };

    const onResetFilters = () => {
        const today = new Date().toISOString().slice(0, 10);
        setFromDate(today);
        setToDate(today);
        setSkillQuery("");
        setAppliedSkillQuery("");

        setSelectedCenters(new Set());
        setAppliedCenters(new Set());

        setAppliedDate({ from: today, to: today });
        setCenterSearch("");
        setCenterOpen(false);

        if (ppcid) getLiveCallWithProcessName(ppcid, today, today);
    };

    const headerTitle = useMemo(() => {
        const d1 = appliedDate.from || fromDate;
        const d2 = appliedDate.to || toDate;
        if (d1 && d2) return `Live Report (${d1} to ${d2})`;
        return "Live Report";
    }, [appliedDate, fromDate, toDate]);

    return (
        <div className="misDashboard today-live-call-page">
            <Helmet>
                <title>SATKARTAR::DASHBOARD</title>
            </Helmet>

            <style>{`
        .skeleton{position:relative;overflow:hidden;background:#eee;border-radius:6px}
        .skeleton:after{content:"";position:absolute;inset:0;transform:translateX(-100%);
          background:linear-gradient(90deg,transparent,rgba(255,255,255,.6),transparent);
          animation:shimmer 1.2s infinite}
        @keyframes shimmer{100%{transform:translateX(100%)}}

        /* Prevent overlap for long skill names */
        .mini-table{ table-layout: fixed; width:100%; }
        .mini-table th,.mini-table td{
          font-size:12px; padding:6px 8px; vertical-align:middle;
        }
        .skillCell{
          white-space:normal !important;
          word-break:break-word;
          overflow-wrap:anywhere;
          line-height:1.15;
        }

        /* sticky header inside card scroll */
        .stickyHead { position: sticky; top: 0; z-index: 2; background: #fff; }
        .skillStickyHeader { background:#fff; }
        .colHead th{ background:#fafafa; }

        .filterRow{display:flex;gap:8px;align-items:end;flex-wrap:wrap;}
        .filterBox{display:flex;flex-direction:column;gap:4px;}
        .filterBox label{font-weight:600;font-size:12px;margin:0;}
        .filterBox select{min-width:120px;}

        /* âœ… Center checkbox dropdown */
        .centerDropWrap{position:relative; min-width:260px; }
        .centerDropBtn{
          width:260px;
          display:flex; align-items:center; justify-content:space-between;
          padding:6px 10px;
          border:1px solid rgba(0,0,0,.15);
          border-radius:6px;
          background:#fff;
          font-size:13px;
        }
        .centerDropMenu{
          position:absolute; top:100%; left:0; z-index:50;
          width:260px;
          background:#fff;
          border:1px solid rgba(0,0,0,.15);
          border-radius:10px;
          margin-top:6px;
          box-shadow:0 10px 30px rgba(0,0,0,.12);
          overflow:hidden;
        }
        .centerDropHead{padding:10px; border-bottom:1px solid rgba(0,0,0,.08);}
        .centerSearch{
          width:100%;
          border:1px solid rgba(0,0,0,.15);
          border-radius:8px;
          padding:7px 10px;
          font-size:13px;
          outline:none;
        }
        .centerActions{
          display:flex; padding:10px;
          border-bottom:1px solid rgba(0,0,0,.08);
          justify-content:space-between;
        }
        .centerList{max-height:240px; overflow:auto; padding:8px 5px;}
        .centerItem{display:flex; align-items:center; gap:10px; padding:6px 2px; font-size:13px;}
        .centerFoot{
          display:flex; gap:8px; justify-content:space-between;
          padding:10px;
          border-top:1px solid rgba(0,0,0,.08);
          background:#fafafa;
        }
      `}</style>

            <ContentWrapper>
                <div className="row">
                    <div className="col-12">
                        <ReportNavBar />
                    </div>
                </div>

                {/* âœ… Top Summary */}
                <div className="live-report-head">
                    <div>
                        <p className="live-report-kicker">Realtime PPC Call View</p>
                        <h5 className="livecallcount">{headerTitle}</h5>
                    </div>
                    <div className="live-summary-strip">
                        <span className="metric-card metric-calls"><i>C</i><em>Calls</em><b>{isLoading ? "..." : fmt(liveCallCount)}</b></span>
                        <span className="metric-card metric-orders"><i>O</i><em>Orders</em><b>{isLoading ? "..." : fmt(liveOrderCount)}</b></span>
                        <span className="metric-card metric-spend"><i>S</i><em>Spend</em><b>{isLoading ? "..." : numberFormatWithGst(liveSpendToday)}</b></span>
                        <span className="metric-card metric-cost"><i>P</i><em>Per Call</em><b className={livePerCall > 100 ? "is-danger" : ""}>{isLoading ? "..." : numberFormatWithGst(livePerCall)}</b></span>
                        <span className="metric-card metric-perf"><i>%</i><em>Perf</em><b>{isLoading ? "..." : `${livePerf.toFixed(1)}%`}</b></span>
                    </div>
                </div>

                {/* âœ… Filters */}
                <div className="card live-filter-card mt-2">
                    <div className="card-body p-2">
                        <div className="filterRow">
                            <div className="filterBox">
                                <label>From Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            <div className="filterBox">
                                <label>To Date</label>
                                <input
                                    type="date"
                                    className="form-control form-control-sm"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            {/* âœ… Center checkbox dropdown */}
                            <div className="filterBox">
                                <label>Center Name</label>
                                <div className="centerDropWrap">
                                    <button
                                        type="button"
                                        className="centerDropBtn"
                                        onClick={() => setCenterOpen((s) => !s)}
                                        disabled={isLoading || !centerOptions.length}
                                    >
                                        <span style={{ fontWeight: 600 }}>{centerButtonText}</span>
                                        <FiChevronDown style={{ opacity: 0.7 }} />
                                    </button>

                                    {centerOpen && (
                                        <div className="centerDropMenu">
                                            <div className="centerDropHead">
                                                <input
                                                    className="centerSearch"
                                                    value={centerSearch}
                                                    onChange={(e) => setCenterSearch(e.target.value)}
                                                    placeholder="Search center..."
                                                />
                                            </div>

                                            <div className="centerList">
                                                {filteredCenterOptions.length ? (
                                                    filteredCenterOptions.map((name) => (
                                                        <label key={name} className="centerItem">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedCenters.has(name)}
                                                                onChange={() => toggleCenter(name)}
                                                            />
                                                            <span>{name}</span>
                                                        </label>
                                                    ))
                                                ) : (
                                                    <div className="text-muted" style={{ fontSize: 13, padding: "8px 2px" }}>
                                                        No centers found
                                                    </div>
                                                )}
                                            </div>

                                            <div className="centerFoot">
                                                <button type="button" className="btnresetall" onClick={onApplyCenters} style={{ width: "100%" }}>
                                                    Apply Centers
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* âœ… Skill search */}
                            <div className="filterBox">
                                <label>Search Skill</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search skill..."
                                    value={skillQuery}
                                    onChange={(e) => setSkillQuery(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            <button className="btnskillapply" onClick={onApplyFilters} disabled={isLoading}>
                                Apply
                            </button>

                            <button className="btnresetall" onClick={onResetFilters} disabled={isLoading}>
                                Reset
                            </button>

                            <button className="btnskill" onClick={() => setShowGroups((s) => !s)} disabled={isLoading}>
                                {showGroups ? "Hide Center View" : "Show Center View"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* âœ… Cards */}
                {showGroups && (
                    <div className="row mt-3 livecallbody">
                        {isLoading
                            ? Array.from({ length: SKELETON_COUNT }).map((_, i) => <SkeletonTableCard key={`sk-${i}`} />)
                            : groupedData.map((group, idx) => (
                                <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={idx}>
                                    <div className="live-center-card">
                                        <div className="live-center-card-head">
                                            <div className="sh-left">{group.name}</div>
                                            <div className="live-center-metrics">
                                                <span className="metric-card metric-calls"><i>C</i><em>Calls</em><b>{fmt(group.totalCalls)}</b></span>
                                                <span className="metric-card metric-orders"><i>O</i><em>Orders</em><b>{fmt(group.totalOrders)}</b></span>
                                                <span className="metric-card metric-spend"><i>S</i><em>Spend</em><b>{numberFormatWithGst(group.totalSpend)}</b></span>
                                                <span className="metric-card metric-cost"><i>P</i><em>Per Call</em><b className={group.perCall > 100 ? "is-danger" : ""}>{numberFormatWithGst(group.perCall)}</b></span>
                                            </div>
                                        </div>
                                        <div className="live-table-scroll">
                                            <table className="table mb-0 mini-table live-skill-table table-bordred">
                                                    <thead className="stickyHead">
                                                        <tr className="colHead">
                                                            <th>Skill</th>
                                                            <th style={{ width: 62, textAlign: "right" }}>Calls</th>
                                                            <th style={{ width: 62, textAlign: "right" }}>Orders</th>
                                                            <th style={{ width: 72, textAlign: "right" }}>Spend</th>
                                                            <th style={{ width: 64, textAlign: "right" }}>Cost</th>
                                                            <th style={{ width: 64, textAlign: "right" }}>Perf</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {group.items.map((item, ix) => {
                                                            const isGood = item.calls > 0 && item.perf >= 15;
                                                            const perfColor = item.calls === 0 ? "#999" : isGood ? "#2e7d32" : "#d32f2f";
                                                            const arrow = item.calls === 0 ? "\u2014" : isGood ? "\u2191" : "\u2193";

                                                            const perCallColor = item.perCall > 100 ? "#d32f2f" : "#111";

                                                            return (
                                                                <tr key={ix}>
                                                                    <td className="skillCell">
                                                                        {getSkillLabel(item)}
                                                                    </td>
                                                                    <td className="num-cell">{fmt(item.calls)}</td>
                                                                    <td className="num-cell">{fmt(item.orders)}</td>
                                                                    <td className="num-cell">{numberFormatWithGst(item.spend)}</td>
                                                                    <td className="num-cell" style={{ color: perCallColor }}>
                                                                        {numberFormatWithGst(item.perCall)}
                                                                    </td>
                                                                    <td className="num-cell">
                                                                        <span
                                                                            className={`perf-pill ${item.calls === 0 ? "perf-neutral" : isGood ? "perf-up" : "perf-down"}`}
                                                                            style={{ color: perfColor }}
                                                                        >
                                                                            {arrow}
                                                                            <span>{item.calls === 0 ? "0.0%" : `${item.perf.toFixed(1)}%`}</span>
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                            ))}

                        {!isLoading && groupedData.length === 0 && (
                            <div className="col-12">
                                <div className="alert alert-info mb-0">No data for selected filters.</div>
                            </div>
                        )}
                    </div>
                )}
            </ContentWrapper>
        </div>
    );
};

export default withAuth(TodayLiveCall);

